import React from "react";
import { fmtCurrency, fmtNumber, fmtPct } from "./analyticsUtils";

const DeltaBadge = ({ value }) => {
    if (value === undefined || value === null) return null;
    const positive = Number(value) >= 0;
    return (
        <span
            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                positive ? "text-green-700 bg-green-50" : "text-red-700 bg-red-50"
            }`}
        >
            <i className={`fa-solid ${positive ? "fa-caret-up" : "fa-caret-down"}`} />
            {fmtPct(value)}
        </span>
    );
};

const Card = ({ label, value, sub, icon, color, delta }) => (
    <div className="bg-white rounded-2xl shadow-1 p-4 flex flex-col gap-2">
        <div className="flex items-center justify-between">
            <span className="text-[12px] uppercase tracking-wide text-slate-500 font-semibold">
                {label}
            </span>
            <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-white ${color}`}>
                <i className={`fa-solid ${icon} text-sm`} />
            </span>
        </div>
        <div className="flex items-end gap-2">
            <span className="text-[24px] leading-none font-bold text-slate-800">{value}</span>
            <DeltaBadge value={delta} />
        </div>
        {sub && <span className="text-[12px] text-slate-400">{sub}</span>}
    </div>
);

const KpiCards = ({ overview, alerts }) => {
    if (!overview) return null;

    const cards = [
        {
            label: "Inventory Value (Cost)",
            value: fmtCurrency(overview.inventory?.total_cost),
            sub: `${fmtNumber(overview.inventory?.units)} units · ${fmtNumber(
                overview.inventory?.sku_count
            )} SKUs`,
            icon: "fa-warehouse",
            color: "bg-[var(--color-2)]",
        },
        {
            label: "Retail Value",
            value: fmtCurrency(overview.inventory?.total_retail),
            sub: `Potential margin ${fmtCurrency(
                (overview.inventory?.total_retail || 0) - (overview.inventory?.total_cost || 0)
            )}`,
            icon: "fa-tags",
            color: "bg-purple-500",
        },
        {
            label: "Sales Revenue",
            value: fmtCurrency(overview.sales?.revenue),
            sub: `${fmtNumber(overview.sales?.units)} units · ${overview.period?.label}`,
            icon: "fa-cash-register",
            color: "bg-green-500",
            delta: overview.sales?.revenue_change_pct,
        },
        {
            label: "Gross Margin",
            value: fmtCurrency(overview.sales?.gross_margin),
            sub: `Purchases ${fmtCurrency(overview.purchases?.cost)}`,
            icon: "fa-coins",
            color: "bg-amber-500",
        },
        {
            label: "Reorder Alerts",
            value: fmtNumber(alerts?.counts?.stockout),
            sub: alerts?.impact
                ? `${fmtCurrency(alerts.impact.potential_lost_revenue)} at risk`
                : "Stockout risk",
            icon: "fa-triangle-exclamation",
            color: "bg-red-500",
        },
        {
            label: "Capital Locked",
            value: fmtCurrency(
                (alerts?.impact?.overstock_capital || 0) + (alerts?.impact?.dead_stock_capital || 0)
            ),
            sub: `${fmtNumber(alerts?.counts?.overstock)} overstock · ${fmtNumber(
                alerts?.counts?.dead_stock
            )} dead`,
            icon: "fa-lock",
            color: "bg-slate-600",
        },
    ];

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            {cards.map((card) => (
                <Card key={card.label} {...card} />
            ))}
        </div>
    );
};

export default KpiCards;
